import React, { createContext, useCallback, useContext, useState } from 'react';
import type { FormInstance } from 'antd';
import type { AIExtractionSnapshot, AgentFieldSource } from '../types';
import type { PopulationGroup } from '../constants/populationGroups';

export interface AgentBusinessContext {
  currentPath?: string;
  workflowStep?: 'patient' | 'model' | 'dose' | 'report' | 'tdm' | null;
  patientContext?: Record<string, any>;
  drugName?: string;
  modelId?: string;
  calcResult?: Record<string, any>;
  tdmData?: Array<Record<string, any>>;
  drugConfig?: Record<string, any>;
  calculationId?: string;
}

type PopulationSource = 'ai' | 'manual' | 'inferred' | null;

export interface PendingFillPayload {
  values: Record<string, any>;
  fieldSources?: Record<string, AgentFieldSource>;
  clearKeys?: string[];
}

interface AgentContextValue {
  drawerOpen: boolean;
  openDrawer: (tab?: string) => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  businessContext: AgentBusinessContext;
  updateContext: (ctx: Partial<AgentBusinessContext>) => void;
  boundForm: FormInstance | null;
  bindForm: (form: FormInstance | null) => void;
  resetWorkspace: (() => void) | null;
  bindResetWorkspace: (resetter: (() => void) | null) => void;
  pendingFill: PendingFillPayload | null;
  setPendingFill: React.Dispatch<React.SetStateAction<PendingFillPayload | null>>;
  activePopulationGroup: PopulationGroup | null;
  setActivePopulationGroup: (group: PopulationGroup | null) => void;
  populationSource: PopulationSource;
  setPopulationSource: (source: PopulationSource) => void;
  populationLocked: boolean;
  setPopulationLocked: (locked: boolean) => void;
  aiExtractionSnapshot: AIExtractionSnapshot | null;
  setAIExtractionSnapshot: React.Dispatch<React.SetStateAction<AIExtractionSnapshot | null>>;
}

const AgentContext = createContext<AgentContextValue | null>(null);

export const useAgent = (): AgentContextValue => {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error('useAgent must be used within AgentProvider');
  return ctx;
};

export const AgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('extract');
  const [businessContext, setBusinessContext] = useState<AgentBusinessContext>({});
  const [boundForm, setBoundForm] = useState<FormInstance | null>(null);
  const [resetWorkspace, setResetWorkspace] = useState<(() => void) | null>(null);
  const [pendingFill, setPendingFill] = useState<PendingFillPayload | null>(null);
  const [activePopulationGroup, setActivePopulationGroup] = useState<PopulationGroup | null>(null);
  const [populationSource, setPopulationSource] = useState<PopulationSource>(null);
  const [populationLocked, setPopulationLocked] = useState(false);
  const [aiExtractionSnapshot, setAIExtractionSnapshot] = useState<AIExtractionSnapshot | null>(null);

  const openDrawer = useCallback((tab?: string) => {
    if (tab) setActiveTab(tab);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen(prev => !prev), []);

  const updateContext = useCallback((ctx: Partial<AgentBusinessContext>) => {
    setBusinessContext(prev => ({ ...prev, ...ctx }));
  }, []);

  const bindForm = useCallback((form: FormInstance | null) => {
    setBoundForm(form);
  }, []);

  const bindResetWorkspace = useCallback((resetter: (() => void) | null) => {
    setResetWorkspace(() => resetter);
  }, []);

  return (
    <AgentContext.Provider value={{
      drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      activeTab,
      setActiveTab,
      businessContext,
      updateContext,
      boundForm,
      bindForm,
      resetWorkspace,
      bindResetWorkspace,
      pendingFill,
      setPendingFill,
      activePopulationGroup,
      setActivePopulationGroup,
      populationSource,
      setPopulationSource,
      populationLocked,
      setPopulationLocked,
      aiExtractionSnapshot,
      setAIExtractionSnapshot,
    }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export default AgentContext;
